import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Rocket, ArrowLeft } from 'lucide-react';
import { studySessionsApi, coursesApi, Course } from '../../services/api';
import { analytics } from '../../services/analytics';
import { MixedText } from '../../utils/rtl';
import { CourseSelector } from './shared/CourseSelector';
import { Card, CardContent, CardHeader, CardTitle, SpaceCard } from '../ui/Card';
import { Button } from '../ui/Button';
import { DashboardContainer } from '../layout/PageContainer';

const QuickCreateStudySession: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [sessionTitle, setSessionTitle] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [newCourseName, setNewCourseName] = useState('');
  const [isCreatingNewCourse, setIsCreatingNewCourse] = useState(false);

  // Load courses and handle URL parameters
  useEffect(() => {
    loadCourses();
    
    // Check for course_id parameter in URL
    const courseIdParam = searchParams.get('course_id');
    if (courseIdParam) {
      const courseId = parseInt(courseIdParam);
      if (!isNaN(courseId)) {
        setSelectedCourseId(courseId);
        setIsCreatingNewCourse(false);
      }
    }
  }, [searchParams]);

  const loadCourses = async () => {
    try {
      setCoursesLoading(true);
      const coursesData = await coursesApi.getAllCourses();
      setCourses(coursesData);
      
      // If no course selected and we have courses, don't auto-select
      // Let user choose explicitly
    } catch (err) {
      setError(t('sessions:create.errors.load_courses_failed'));
      console.error('Error loading courses:', err);
    } finally {
      setCoursesLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!sessionTitle.trim()) {
      setError(t('sessions:create.errors.title_required'));
      return false;
    }

    if (isCreatingNewCourse) {
      if (!newCourseName.trim() || newCourseName.trim().length < 3) {
        setError(t('courses:create.errors.name_required'));
        return false;
      }
    } else {
      if (!selectedCourseId) {
        setError(t('sessions:create.errors.course_required'));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    let finalCourseId = selectedCourseId;

    try {
      setLoading(true);
      setError(null);
      
      // Create new course if needed
      if (isCreatingNewCourse) {
        const newCourse = await coursesApi.createCourse({
          name: newCourseName.trim(),
          description: '',
        });
        
        finalCourseId = newCourse.id;
        
        // Track course creation
        analytics.track('Course Created from Quick Action', {
          course_id: newCourse.id,
          course_name: newCourseName.trim(),
          source: 'quick_create_session'
        });
      }
      
      // Create study session
      const session = await studySessionsApi.createSession({
        title: sessionTitle.trim(),
        course_id: finalCourseId!
      });
      
      // Track session creation
      analytics.track('Study Session Created from Quick Action', {
        session_id: session.id,
        course_id: finalCourseId,
        session_title: sessionTitle.trim(),
        source: 'quick_create_session'
      });
      
      // Navigate to session upload step
      navigate(`/session/${session.id}`);
      
    } catch (err: any) {
      console.error('Error creating session:', err);
      
      // Skip handling if credit error was already handled by interceptor
      if (err?.name === 'CreditErrorHandled') {
        return;
      }
      
      // Re-throw 402 errors to let the interceptor handle them
      if (err?.response?.status === 402) {
        throw err;
      }
      
      setError(t('sessions:create.errors.creation_failed'));
      analytics.trackError('Quick Session Creation Failed', {
        session_title: sessionTitle.trim(),
        course_id: finalCourseId,
        is_creating_new_course: isCreatingNewCourse
      });
    } finally {
      setLoading(false);
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  return (
    <DashboardContainer>
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-6">
          {/* Breadcrumb Navigation */}
          <button
            onClick={() => navigate('/')}
            className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
          >
            {isRTL ? (
              <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            ) : (
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            )}
            <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{t('common:buttons.back')}</MixedText>
            </span>
          </button>

          {/* Page Title */}
          <SpaceCard constellation="cassiopeia" className="group hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                <div className="w-16 h-16 bg-gradient-to-br from-vibrant-orange/20 to-muted-purple/20 rounded-2xl flex items-center justify-center relative">
                  <Rocket className="w-8 h-8 text-vibrant-orange" />
                </div>
                <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                  <CardTitle className={`text-3xl font-bold mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('sessions:create.title')}</MixedText>
                  </CardTitle>
                  <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('sessions:create.subtitle')}</MixedText>
                  </p>
                </div>
              </div>
            </CardHeader>
          </SpaceCard>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-vibrant-orange/30 bg-vibrant-orange/10">
            <CardContent>
              <div className="flex items-center">
                <div className={`w-5 h-5 text-vibrant-orange ${isRTL ? 'ml-3' : 'mr-3'}`}>⚠️</div>
                <p className={`text-vibrant-orange font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{error}</MixedText>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Form */}
        <SpaceCard constellation="orion">
          <CardContent>
            <div className="max-w-3xl mx-auto space-y-4 md:space-y-8">
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                {/* Course Selection */}
                <CourseSelector
                  selectedCourseId={selectedCourseId}
                  newCourseName={newCourseName}
                  onCourseSelect={setSelectedCourseId}
                  onNewCourseNameChange={setNewCourseName}
                  isCreatingNew={isCreatingNewCourse}
                  onToggleCreateNew={() => setIsCreatingNewCourse(!isCreatingNewCourse)}
                  existingCourses={courses}
                  isRTL={isRTL}
                  loading={coursesLoading || loading}
                  disabled={loading}
                />

                {/* Session Title */}
                <div>
                  <label htmlFor="sessionTitle" className={`block text-lg font-semibold text-surface-primary mb-3 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      🎯 {t('sessions:create.form.title')}
                    </span>
                  </label>
                  <input
                    type="text"
                    id="sessionTitle"
                    value={sessionTitle}
                    onChange={(e) => setSessionTitle(e.target.value)}
                    placeholder={t('sessions:create.form.title_placeholder')}
                    disabled={loading}
                    className={`w-full px-4 py-3 border-2 border-surface-muted/30 rounded-lg focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all duration-200 font-hebrew bg-surface-muted/5 backdrop-blur-sm ${isRTL ? 'text-right' : ''} ${loading ? 'opacity-50' : ''}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-2 md:pt-4 space-y-3">
                  {/* Availability Notice */}
                  <div className={`text-center text-sm text-surface-muted font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <span className="inline-flex items-center">
                      💡 <MixedText>{t('sessions:create.form.availability_notice')}</MixedText>
                    </span>
                  </div>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={loading || coursesLoading}
                    loading={loading}
                    className="w-full font-hebrew"
                    glow
                  >
                    <Rocket className="w-5 h-5" />
                    <MixedText>
                      {loading ? t('sessions:create.form.creating') : t('sessions:create.form.submit')}
                    </MixedText>
                  </Button>
                </div>
              </form>
            </div>
          </CardContent>
        </SpaceCard>
      </div>
    </DashboardContainer>
  );
};

export default QuickCreateStudySession; 